/*
 * Free shipping bar — live refresh.
 *
 * The section (sections/free-shipping-bar.liquid) is the only source of truth: this file never
 * computes eligibility. After a cart change it re-fetches the section via the Section Rendering
 * API for the CURRENT page URL (so product/collection/template targeting stays correct) and morphs
 * the message, progress bar and visibility in place.
 *
 * Cart-change detection (Turbo 9.5 has no cart events):
 *  1. PerformanceObserver on resource timings for /cart/add|change|update|clear — this is the
 *     primary hook. It sees Turbo's own jQuery XHR calls (app.js.liquid calls its private
 *     refreshCart closure directly, NOT window.refreshCart, so a wrapper alone would miss them),
 *     and it sees app embeds (Prefixbox, Zoorix) that write to the cart without touching the theme.
 *     The theme already uses this pattern for TrueFit in layout/theme.liquid. A refresh after a
 *     failed mutation simply re-renders the true server state, so it can never show a false success.
 *  2. window.refreshCart — custom-options.js and club-kit-builder.js call it after their atomic
 *     adds. Defined as a property with a setter so the hook survives InstantClick (ludicrous mode)
 *     re-executing app.js and reassigning it.
 *  Cart-page line changes submit the form (full reload) — nothing to do.
 *
 * Hooks install once per window; the root binding re-runs on theme-editor section reloads and
 * InstantClick page changes.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 150;
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var STORAGE_KEY = 'gks-fsb-dismissed';
  var SECTION_ID = 'free-shipping-bar';

  if (window.GKSFreeShippingBar) {
    window.GKSFreeShippingBar.bind();
    return;
  }

  var state = {
    root: null,
    seq: 0,
    timer: null,
    controller: null,
    resizeObserver: null,
    dismissed: false,
    suppressed: false,      // true between an editor section unload and the next load
    priorInlinePadding: null, // body.style.padding-bottom before we touched it (null = untouched)
  };

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function readDismissed() {
    if (designMode()) return false; // always show the bar to the merchant in the editor
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { return false; }
  }

  function root() {
    if (!state.root || !document.documentElement.contains(state.root)) {
      state.root = document.querySelector('[data-gks-fsb]');
    }
    return state.root;
  }

  // ------------------------------------------------------------ spacing
  function isVisible(el) {
    return !!el && !state.suppressed && !el.hidden && !state.dismissed && el.getClientRects().length > 0;
  }

  // Adds the bar's measured height ON TOP of whatever bottom padding the body already has (theme or
  // other apps), as an inline !important declaration; removing it restores the previous inline value.
  function syncSpacing() {
    var el = root();
    var body = document.body;
    if (!body) return;
    // Theme-preview mode only: Shopify's preview strip (#PBarNextFrameWrapper) covers the viewport
    // bottom and has no close control on phones, so sit the bar on top of it. Absent on the live theme.
    var pb = document.getElementById('PBarNextFrameWrapper');
    var pbH = pb ? Math.ceil(pb.getBoundingClientRect().height) : 0;
    if (el) el.style.bottom = pbH ? pbH + 'px' : '';
    if (isVisible(el)) {
      if (state.priorInlinePadding === null) state.priorInlinePadding = body.style.getPropertyValue('padding-bottom') || '';
      // Measure the base with our own declaration removed so stylesheet padding from elsewhere is kept.
      body.style.setProperty('padding-bottom', state.priorInlinePadding);
      var base = parseFloat(getComputedStyle(body).paddingBottom) || 0;
      var h = Math.ceil(el.getBoundingClientRect().height) + pbH; // include the preview strip so the chat bubble clears both
      body.style.setProperty('padding-bottom', 'calc(' + base + 'px + ' + h + 'px)', 'important');
      document.documentElement.style.setProperty('--gks-fsb-h', h + 'px');
      body.classList.add('gks-fsb-visible');
    } else {
      if (state.priorInlinePadding !== null) {
        if (state.priorInlinePadding) body.style.setProperty('padding-bottom', state.priorInlinePadding);
        else body.style.removeProperty('padding-bottom');
        state.priorInlinePadding = null;
      }
      document.documentElement.style.removeProperty('--gks-fsb-h');
      body.classList.remove('gks-fsb-visible');
    }
  }

  function observeSize(el) {
    if (state.resizeObserver) state.resizeObserver.disconnect();
    if (!el || !('ResizeObserver' in window)) return;
    state.resizeObserver = new ResizeObserver(function () { syncSpacing(); });
    state.resizeObserver.observe(el);
  }

  // ------------------------------------------------------------ dismiss
  function dismiss() {
    state.dismissed = true;
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* private mode etc. — hide for this page only */ }
    document.documentElement.classList.add('gks-fsb-dismissed');
    syncSpacing();
  }

  // ------------------------------------------------------------ morph
  function applyContent(cur, next) {
    var curMsg = cur.querySelector('.gks-fsb__msg');
    var nextMsg = next.querySelector('.gks-fsb__msg');
    if (curMsg && nextMsg && curMsg.innerHTML !== nextMsg.innerHTML) {
      // Only touch the live region when the text actually changed, so screen readers
      // don't re-announce an unchanged message.
      curMsg.innerHTML = nextMsg.innerHTML;
      var amt = curMsg.querySelector('.gks-fsb__amount');
      if (amt) {
        amt.classList.add('is-updated');
        amt.addEventListener('animationend', function () { amt.classList.remove('is-updated'); }, { once: true });
      }
    }

    // Optional CTA link: present only in the progress state.
    var curCta = cur.querySelector('.gks-fsb__cta');
    var nextCta = next.querySelector('.gks-fsb__cta');
    if (nextCta && !curCta) {
      var statusEl = cur.querySelector('.gks-fsb__status');
      if (statusEl) statusEl.insertAdjacentElement('afterend', nextCta);
    } else if (!nextCta && curCta) {
      curCta.parentNode.removeChild(curCta);
    } else if (nextCta && curCta && curCta.outerHTML !== nextCta.outerHTML) {
      curCta.replaceWith(nextCta);
    }

    var curTrack = cur.querySelector('.gks-fsb__track');
    var nextTrack = next.querySelector('.gks-fsb__track');
    if (nextTrack && curTrack) {
      var nf = nextTrack.querySelector('.gks-fsb__fill');
      var cf = curTrack.querySelector('.gks-fsb__fill');
      if (nf && cf && cf.style.width !== nf.style.width) cf.style.width = nf.style.width;
    } else if (nextTrack && !curTrack) {
      var status = cur.querySelector('.gks-fsb__status');
      if (status) status.insertAdjacentElement('afterend', nextTrack);
    } else if (!nextTrack && curTrack) {
      curTrack.parentNode.removeChild(curTrack);
    }

    var curIcon = cur.querySelector('.gks-fsb__icon');
    var nextIcon = next.querySelector('.gks-fsb__icon');
    if (curIcon && nextIcon && curIcon.innerHTML !== nextIcon.innerHTML) curIcon.innerHTML = nextIcon.innerHTML;

    var curData = cur.querySelector('[data-gks-fsb-data]');
    var nextData = next.querySelector('[data-gks-fsb-data]');
    if (curData && nextData) curData.textContent = nextData.textContent;
    cur.setAttribute('data-state', next.getAttribute('data-state') || 'hidden');
    syncSpacing();
  }

  function morph(next) {
    var cur = root();
    if (!cur) return;
    if (!next) {
      // Section rendered nothing for this URL (disabled / excluded page): hide.
      cur.hidden = true;
      cur.setAttribute('data-state', 'hidden');
      syncSpacing();
      return;
    }
    var nextHidden = next.hasAttribute('hidden');
    if (cur.hidden && !nextHidden) {
      // Un-hide first, then change the live region on the next frame so the new message is
      // announced (changes inside display:none are not).
      cur.hidden = false;
      window.requestAnimationFrame(function () { applyContent(cur, next); });
      return;
    }
    cur.hidden = nextHidden;
    applyContent(cur, next);
  }

  // ------------------------------------------------------------ refresh
  function sectionUrl() {
    var el = root();
    var id = (el && el.getAttribute('data-section-id')) || SECTION_ID;
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', id);
    url.hash = '';
    return url.toString();
  }

  function refreshNow() {
    if (!root()) return;
    var mySeq = ++state.seq;
    if (state.controller) state.controller.abort();
    var controller = ('AbortController' in window) ? new AbortController() : null;
    state.controller = controller;

    fetch(sectionUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'text/html' },
      signal: controller ? controller.signal : undefined,
    }).then(function (res) {
      if (!res.ok) throw new Error('section ' + res.status);
      return res.text();
    }).then(function (html) {
      if (mySeq !== state.seq) return; // a newer refresh superseded this one
      var doc = new DOMParser().parseFromString(html, 'text/html');
      morph(doc.querySelector('[data-gks-fsb]'));
    }).catch(function () {
      // Network error / aborted / non-200: keep the last verified state.
    });
  }

  function refresh() {
    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      state.timer = null;
      refreshNow();
    }, DEBOUNCE_MS);
  }

  // ------------------------------------------------------------ hooks (once per window)
  function isOurs(e) {
    var id = e && e.detail && e.detail.sectionId;
    if (id === SECTION_ID) return true;
    return !!(e && e.target && e.target.querySelector && e.target.querySelector('[data-gks-fsb]'));
  }

  function installHooks() {
    if (window.__gksFsbHooks) return;
    window.__gksFsbHooks = true;

    // 1. Resource timing — primary cart-change signal (see header comment).
    if ('PerformanceObserver' in window) {
      try {
        var po = new PerformanceObserver(function (list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            var e = entries[i];
            if ((e.initiatorType === 'fetch' || e.initiatorType === 'xmlhttprequest') && CART_MUTATION.test(e.name)) {
              refresh();
              break;
            }
          }
        });
        po.observe({ entryTypes: ['resource'] });
      } catch (e) { /* ignore */ }
    }

    // 2. window.refreshCart — property with setter so later reassignments (app.js re-executed by
    //    InstantClick, or loaded after this file) are wrapped automatically. `chain` keeps every
    //    function ever assigned, so if a third party wraps us and then calls us back, the re-entered
    //    call runs the previous function instead of recursing.
    var chain = typeof window.refreshCart === 'function' ? [window.refreshCart] : [];
    var depth = 0;
    function wrapped() {
      var fn = chain[chain.length - 1 - depth];
      var result;
      if (fn) {
        depth++;
        try { result = fn.apply(this, arguments); } finally { depth--; }
      }
      if (depth === 0) refresh();
      return result;
    }
    wrapped.__gksFsb = true;
    try {
      Object.defineProperty(window, 'refreshCart', {
        configurable: true,
        enumerable: true,
        get: function () { return wrapped; },
        set: function (fn) { if (typeof fn === 'function' && !fn.__gksFsb) chain.push(fn); },
      });
    } catch (e) {
      window.refreshCart = wrapped;
    }

    // 3. Theme editor: this section re-rendered (settings change / enable toggle / exclusions).
    document.addEventListener('shopify:section:load', function (e) {
      if (!isOurs(e)) return;
      state.suppressed = false;
      bind();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (!isOurs(e)) return;
      state.suppressed = true;
      state.root = null;
      if (state.resizeObserver) state.resizeObserver.disconnect();
      syncSpacing();
    });

    // 4. InstantClick (Turbo "ludicrous" mode) swaps <body>; re-bind to the new root.
    if (window.InstantClick && typeof window.InstantClick.on === 'function') {
      try { window.InstantClick.on('change', bind); } catch (e) { /* ignore */ }
    }
  }

  // ------------------------------------------------------------ bind
  function bind() {
    state.root = null;
    state.suppressed = false;
    var el = root();
    state.dismissed = readDismissed();
    if (!el) { syncSpacing(); return; }
    if (state.dismissed) document.documentElement.classList.add('gks-fsb-dismissed');
    if (!el.__gksFsbBound) {
      el.__gksFsbBound = true;
      var close = el.querySelector('[data-gks-fsb-close]');
      if (close) close.addEventListener('click', dismiss);
    }
    observeSize(el);
    syncSpacing();
    // The preview strip is injected a moment after load; re-measure a couple of times.
    setTimeout(syncSpacing, 1500);
    setTimeout(syncSpacing, 4000);
  }

  window.GKSFreeShippingBar = { bind: bind, refresh: refresh, dismiss: dismiss };
  installHooks();
  bind();
})();
